<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import lumeratecConstants from 'dashboard/constants/globals';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  ACCESSIBILITY_UI_SETTINGS_KEY,
  createDefaultAccessibilityPreferences,
  normalizeAccessibilityPreferences,
} from './accessibilityPreferences';
import {
  getConversationContactName,
  isEditableElement,
  rankAssignmentCandidates,
} from './callCenterAccessibility';

const route = useRoute();
const router = useRouter();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const selectedChat = useMapGetter('getSelectedChat');
const { uiSettings } = useUISettings();

const isOpen = ref(false);
const query = ref('');
const activeIndex = ref(0);
const isAssigning = ref(false);
const searchInput = ref(null);
const dialog = ref(null);
const lastFocusedElement = ref(null);
let landmarkIndex = -1;

const copy = {
  title: 'Central de ações acessível',
  description:
    'Pesquise uma ação. Use setas para escolher, Enter para executar e Escape para fechar.',
  close: 'Fechar',
  closeLabel: 'Fechar central de ações',
  searchLabel: 'Pesquisar ação',
  searchPlaceholder: 'Ex.: assumir conversa, tickets ou responder',
  empty: 'Nenhuma ação encontrada.',
  actionsLabel: 'Ações disponíveis',
};

const preferences = computed(() =>
  normalizeAccessibilityPreferences(
    uiSettings.value?.[ACCESSIBILITY_UI_SETTINGS_KEY] ||
      createDefaultAccessibilityPreferences()
  )
);

const accountId = computed(
  () => route.params.accountId || currentAccountId.value
);

const announce = (message, priority = 'polite') => {
  window.dispatchEvent(
    new CustomEvent('a11y-announce', { detail: { message, priority } })
  );
};

const requestExistingAction = action => {
  window.dispatchEvent(new CustomEvent('a11y-command', { detail: { action } }));
};

const navigateTo = async (name, label) => {
  await router.push({ name, params: { accountId: accountId.value } });
  announce(`Página ${label}.`);
};

const updateAssignee = async (conversation, agent, successMessage) => {
  const response = await ConversationApi.assignAgent({
    conversationId: conversation.id,
    agentId: agent?.id || null,
  });
  await store.dispatch('setCurrentChatAssignee', {
    conversationId: conversation.id,
    assignee: agent ? response.data || agent : null,
  });
  announce(successMessage, 'assertive');
};

const assignCurrentConversation = async () => {
  if (!selectedChat.value?.id) {
    announce('Nenhuma conversa está aberta para assumir.', 'assertive');
    return;
  }
  if (selectedChat.value.meta?.assignee?.id === currentUser.value?.id) {
    announce('Esta conversa já está atribuída a você.');
    return;
  }

  try {
    isAssigning.value = true;
    await updateAssignee(
      selectedChat.value,
      currentUser.value,
      `Conversa com ${getConversationContactName(selectedChat.value)} atribuída a você.`
    );
  } catch (error) {
    announce(
      error.response?.data?.message || 'Não foi possível assumir a conversa.',
      'assertive'
    );
  } finally {
    isAssigning.value = false;
  }
};

const returnCurrentConversationToQueue = async () => {
  if (!selectedChat.value?.id) {
    announce('Nenhuma conversa está aberta para devolver à fila.', 'assertive');
    return;
  }

  try {
    isAssigning.value = true;
    const contactName = getConversationContactName(selectedChat.value);
    await updateAssignee(
      selectedChat.value,
      null,
      `Conversa com ${contactName} devolvida à fila de não atribuídas.`
    );
  } catch (error) {
    announce(
      error.response?.data?.message ||
        'Não foi possível devolver a conversa à fila.',
      'assertive'
    );
  } finally {
    isAssigning.value = false;
  }
};

const claimNextConversation = async () => {
  if (isAssigning.value) {
    announce('Uma atribuição já está em andamento.');
    return;
  }

  try {
    isAssigning.value = true;
    announce('Buscando a próxima conversa da fila.');
    const response = await ConversationApi.get({
      status: lumeratecConstants.STATUS_TYPE.OPEN,
      assigneeType: lumeratecConstants.ASSIGNEE_TYPE.UNASSIGNED,
      page: 1,
      sortBy: lumeratecConstants.SORT_BY_TYPE.PRIORITY_DESC_CREATED_AT_ASC,
    });
    const payload =
      response.data?.data?.payload || response.data?.payload || [];
    const [conversation] = rankAssignmentCandidates(payload);

    if (!conversation) {
      announce('Não há conversas disponíveis para assumir.', 'assertive');
      return;
    }

    await updateAssignee(
      conversation,
      currentUser.value,
      'Conversa atribuída.'
    );
    await router.push(
      frontendURL(
        conversationUrl({
          accountId: accountId.value,
          id: conversation.id,
        })
      )
    );
    announce(
      `Próxima conversa assumida. ${getConversationContactName(conversation)}.${
        conversation.sla_missed_events?.length ? ' Atenção, SLA violado.' : ''
      }`,
      'assertive'
    );
  } catch (error) {
    announce(
      error.response?.data?.message ||
        'Não foi possível buscar e assumir a próxima conversa.',
      'assertive'
    );
  } finally {
    isAssigning.value = false;
  }
};

const actions = computed(() => [
  {
    id: 'claim-next',
    label: 'Pegar próxima conversa da fila',
    description: 'Prioriza SLA violado, urgência e maior tempo de espera.',
    shortcut: 'Alt Shift G',
    keywords: 'assumir pegar fila atendimento sla',
    run: claimNextConversation,
  },
  {
    id: 'assign-current',
    label: 'Atribuir conversa atual a mim',
    description: 'Assume imediatamente a conversa aberta.',
    shortcut: 'Alt Shift U',
    keywords: 'assumir atribuir atual mim',
    run: assignCurrentConversation,
  },
  {
    id: 'return-queue',
    label: 'Devolver conversa atual à fila',
    description: 'Remove a atribuição sem resolver a conversa.',
    shortcut: 'Alt Shift Q',
    keywords: 'devolver remover atribuição fila',
    run: returnCurrentConversationToQueue,
  },
  {
    id: 'briefing',
    label: 'Ouvir situação da conversa atual',
    description: 'Anuncia contato, status, prioridade e SLA.',
    shortcut: 'Alt Shift L',
    keywords: 'briefing situação conversa sla',
    run: () => requestExistingAction('briefing'),
  },
  {
    id: 'summary',
    label: 'Resumir conversa atual',
    description: 'Gera e anuncia um resumo real pela LumerAI.',
    shortcut: 'Alt Shift S',
    keywords: 'resumo lumerai conversa',
    run: () => requestExistingAction('summary'),
  },
  {
    id: 'describe-image',
    label: 'Descrever próxima imagem recebida',
    description: 'Gera uma descrição visual e a adiciona à conversa.',
    shortcut: 'Alt Shift I',
    keywords: 'imagem foto anexo descrição',
    run: () => requestExistingAction('describe-image'),
  },
  {
    id: 'focus-conversations',
    label: 'Ir para a lista de conversas',
    description: 'Move o foco sem percorrer toda a página com Tab.',
    shortcut: 'Alt Shift C',
    keywords: 'foco lista conversas',
    run: () => requestExistingAction('focus-conversation-list'),
  },
  {
    id: 'focus-reply',
    label: 'Ir para o campo de resposta',
    description: 'Move o foco diretamente para o editor de mensagem.',
    shortcut: 'Alt Shift R',
    keywords: 'foco responder editor mensagem',
    run: () => requestExistingAction('focus-reply'),
  },
  ...[
    ['home', 'Conversas', 'conversas atendimento caixa'],
    ['contacts_dashboard_index', 'Contatos', 'contatos clientes'],
    ['campaigns_whatsapp_index', 'Campanhas', 'campanhas whatsapp disparo'],
    ['sms_configuration', 'SMS', 'sms disparo'],
    ['tickets_dashboard_index', 'Tickets', 'tickets chamados'],
    ['locations_index', 'Locais e Cobertura', 'locais cobertura'],
    ['helpcenter_viewer', 'Central de Ajuda', 'ajuda artigos'],
    ['account_overview_reports', 'Relatórios', 'relatórios indicadores'],
    ['billing_settings_index', 'Lumera App Store', 'loja plano módulos'],
    ['lumerai_settings_index', 'LumerAI', 'lumerai inteligência artificial'],
    ['lumerai_flows', 'Workflow', 'workflow fluxos'],
    ['agent_list', 'Gestão de Usuários', 'agentes usuários equipes'],
    ['general_settings_index', 'Configurações', 'configurações conta'],
  ].map(([name, label, keywords]) => ({
    id: `navigate-${name}`,
    label: `Abrir ${label}`,
    description: `Navegar para ${label}.`,
    keywords,
    run: () => navigateTo(name, label),
  })),
]);

const filteredActions = computed(() => {
  const normalizedQuery = query.value.trim().toLocaleLowerCase('pt-BR');
  if (!normalizedQuery) return actions.value;
  return actions.value.filter(action =>
    `${action.label} ${action.description} ${action.keywords || ''}`
      .toLocaleLowerCase('pt-BR')
      .includes(normalizedQuery)
  );
});

const closeActionCenter = () => {
  isOpen.value = false;
  query.value = '';
  activeIndex.value = 0;
  nextTick(() => lastFocusedElement.value?.focus?.());
};

const openActionCenter = async () => {
  lastFocusedElement.value = document.activeElement;
  isOpen.value = true;
  await nextTick();
  searchInput.value?.focus();
  announce('Central de ações aberta. Digite para pesquisar uma ação.');
};

const executeAction = async action => {
  if (!action) return;
  closeActionCenter();
  await action.run();
};

const focusLandmark = direction => {
  const definitions = [
    ['aside, [role="complementary"]', 'Menu principal'],
    ['#conversation-list-region', 'Lista de conversas'],
    ['[aria-label="Conversa atual"]', 'Conversa atual'],
    ['#reply-composer', 'Campo de resposta'],
    ['#main-content, main', 'Conteúdo principal'],
  ];
  const landmarks = definitions
    .map(([selector, label]) => ({
      element: document.querySelector(selector),
      label,
    }))
    .filter(
      ({ element }, index, list) =>
        element && list.findIndex(item => item.element === element) === index
    );

  if (!landmarks.length) {
    announce('Nenhuma região de navegação foi encontrada nesta tela.');
    return;
  }

  const containingIndex = landmarks.findIndex(({ element }) =>
    element.contains(document.activeElement)
  );
  if (containingIndex >= 0) landmarkIndex = containingIndex;
  landmarkIndex =
    (landmarkIndex + direction + landmarks.length) % landmarks.length;
  const landmark = landmarks[landmarkIndex];
  if (!landmark.element.hasAttribute('tabindex')) {
    landmark.element.setAttribute('tabindex', '-1');
    landmark.element.dataset.a11yTemporaryTabindex = 'true';
  }
  landmark.element.focus({ preventScroll: true });
  landmark.element.scrollIntoView?.({ block: 'nearest' });
  announce(
    `${landmark.label}. Região ${landmarkIndex + 1} de ${landmarks.length}.`
  );
};

const handleDialogKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeActionCenter();
    return;
  }
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    if (!filteredActions.value.length) return;
    event.preventDefault();
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    activeIndex.value =
      (activeIndex.value + offset + filteredActions.value.length) %
      filteredActions.value.length;
    return;
  }
  if (event.key === 'Enter' && document.activeElement === searchInput.value) {
    event.preventDefault();
    executeAction(filteredActions.value[activeIndex.value]);
    return;
  }
  if (event.key !== 'Tab') return;

  const focusable = [...dialog.value.querySelectorAll('button, input')].filter(
    element => !element.disabled
  );
  const first = focusable[0];
  const last = focusable.at(-1);
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const handleGlobalKeydown = event => {
  if (event.key === 'F2') {
    event.preventDefault();
    if (isOpen.value) {
      closeActionCenter();
    } else {
      openActionCenter();
    }
    return;
  }
  if (isOpen.value || event.defaultPrevented) return;
  if (!preferences.value.keyboardNavigation) return;

  if (event.key === 'F6') {
    event.preventDefault();
    focusLandmark(event.shiftKey ? -1 : 1);
    return;
  }

  if (!event.altKey || !event.shiftKey || isEditableElement(event.target)) {
    return;
  }

  const action = {
    KeyG: claimNextConversation,
    KeyU: assignCurrentConversation,
    KeyQ: returnCurrentConversationToQueue,
  }[event.code];
  if (!action) return;
  event.preventDefault();
  action();
};

watch(filteredActions, () => {
  activeIndex.value = 0;
});

watch(
  () => route.fullPath,
  () => {
    if (isOpen.value) closeActionCenter();
    landmarkIndex = -1;
  }
);

onMounted(() => window.addEventListener('keydown', handleGlobalKeydown));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalKeydown);
  document
    .querySelectorAll('[data-a11y-temporary-tabindex="true"]')
    .forEach(element => element.removeAttribute('tabindex'));
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[100001] flex items-start justify-center p-4 pt-[10vh] bg-black/70"
      @click.self="closeActionCenter"
    >
      <section
        ref="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="a11y-action-center-title"
        aria-describedby="a11y-action-center-description"
        class="w-full max-w-2xl overflow-hidden border rounded-xl shadow-2xl border-n-strong bg-n-solid-1 text-n-slate-12"
        @keydown="handleDialogKeydown"
      >
        <header
          class="flex items-start justify-between gap-4 p-5 border-b border-n-weak"
        >
          <div>
            <h1 id="a11y-action-center-title" class="text-xl font-semibold">
              {{ copy.title }}
            </h1>
            <p
              id="a11y-action-center-description"
              class="mt-1 text-sm text-n-slate-11"
            >
              {{ copy.description }}
            </p>
          </div>
          <button
            type="button"
            class="px-3 py-2 rounded-lg hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :aria-label="copy.closeLabel"
            @click="closeActionCenter"
          >
            {{ copy.close }}
          </button>
        </header>

        <div class="p-4">
          <label for="a11y-action-search" class="sr-only">{{
            copy.searchLabel
          }}</label>
          <input
            id="a11y-action-search"
            ref="searchInput"
            v-model="query"
            type="search"
            autocomplete="off"
            class="w-full px-4 py-3 border rounded-lg border-n-strong bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :placeholder="copy.searchPlaceholder"
            aria-controls="a11y-action-list"
            :aria-activedescendant="
              filteredActions[activeIndex]
                ? `a11y-action-${filteredActions[activeIndex].id}`
                : undefined
            "
          />
        </div>

        <p v-if="!filteredActions.length" role="status" class="px-5 pb-5">
          {{ copy.empty }}
        </p>
        <ul
          v-else
          id="a11y-action-list"
          role="listbox"
          :aria-label="copy.actionsLabel"
          class="max-h-[55vh] px-2 pb-3 overflow-y-auto"
        >
          <li
            v-for="(action, index) in filteredActions"
            :key="action.id"
            role="presentation"
          >
            <button
              :id="`a11y-action-${action.id}`"
              type="button"
              role="option"
              :aria-selected="index === activeIndex"
              class="flex items-center justify-between w-full gap-4 px-4 py-3 text-left rounded-lg"
              :class="
                index === activeIndex
                  ? 'bg-n-brand text-white'
                  : 'hover:bg-n-alpha-2'
              "
              @focus="activeIndex = index"
              @mouseenter="activeIndex = index"
              @click="executeAction(action)"
            >
              <span>
                <span class="block font-medium">{{ action.label }}</span>
                <span class="block mt-1 text-sm opacity-80">{{
                  action.description
                }}</span>
              </span>
              <kbd
                v-if="action.shortcut"
                class="shrink-0 px-2 py-1 text-xs border rounded border-current"
              >
                {{ action.shortcut }}
              </kbd>
            </button>
          </li>
        </ul>
      </section>
    </div>
  </Teleport>
</template>
